import React, { useEffect } from 'react'
import { pipe } from 'fp-ts/es6/function'
import * as O from 'fp-ts/es6/Option'
import { Treatment, WeblabTreatment } from '../types/weblab-types'

interface JamSessionContextValues {
  getWeblabTreatment: (weblab: string) => Treatment
}

export const JamSessionContext = React.createContext<JamSessionContextValues>({
  getWeblabTreatment: () => 'C',
})

export interface WeblabCache {
  get: (principal: string, weblab: string) => O.Option<WeblabTreatment>
  put: (principal: string, weblabTreatment: WeblabTreatment) => void
}

export interface WeblabInitResult {
  weblabs: WeblabTreatment[]
  principal: string
}

export type WeblabInitFn = (principal: string) => Promise<WeblabInitResult>

interface JamSessionProviderProps {
  principal: string
}

type JamSessionProviderComponent = React.FunctionComponent<React.PropsWithChildren<JamSessionProviderProps>>

export const createJamSessionProvider = (init: WeblabInitFn, cache: WeblabCache): JamSessionProviderComponent => {
  return function WeblabsProvider(props: React.PropsWithChildren<JamSessionProviderProps>) {
    const handleResult = (result: WeblabInitResult): void => {
      result.weblabs.forEach(e => {
        cache.put(result.principal, e)
      })
    }

    useEffect(() => {
      // Since this is async, we do not guarantee that the values are ready on the first get.
      // The cached values will likely be loaded first.
      // This is intentional, we do not want the app to block for the weblabs to load.
      init(props.principal)
        .catch(() => null)
        .then(result => {
          if (result) {
            handleResult(result)
          }
        })
    }, [props.principal])

    const getWeblabTreatment = (weblab: string): Treatment => {
      return pipe(
        cache.get(props.principal, weblab),
        O.map(e => e.treatment),
        O.getOrElse(() => 'C' as Treatment),
      )
    }

    return (
      <JamSessionContext.Provider
        value={{
          getWeblabTreatment,
        }}
      >
        {props.children}
      </JamSessionContext.Provider>
    )
  }
}
